import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Store } from 'lucide-react';
import ListingCard from '@/components/marketplace/ListingCard';
import { browseListings } from '@/lib/marketplace';
import { Tile, Eyebrow } from '@/components/bento';
import { MARKETPLACE, percent } from '@/config/earnings';

// ─── The marketplace, on the homepage and the dashboard ──────────────────────
// A row of sites for sale, with the way in to browse and the way in to sell.
// Says nothing at all while there is nothing listed rather than showing an
// empty shop window — except the invitation to be the first seller, when
// `inviteWhenEmpty` is set (the dashboard, where every visitor can sell).

export default function MarketplaceShowcase({ className = '', limit = 3, inviteWhenEmpty = false, onSell = null }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    browseListings({ sort: 'newest', limit })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ listings: [], total: 0 }); });
    return () => { cancelled = true; };
  }, [limit]);

  if (!data) return null;
  if (!data.listings.length && !inviteWhenEmpty) return null;

  const pricing = { symbol: data.symbol, locale: data.locale, currency: data.currency };
  const keep = percent(1 - MARKETPLACE.commissionRate);

  return (
    <Tile className={`p-5 sm:p-8 text-left ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <Eyebrow>Marketplace</Eyebrow>
          <h2 className="mt-4 text-white font-extrabold tracking-tight text-2xl sm:text-3xl">Ready-made sites, for sale</h2>
          <p className="mt-2 text-zinc-400 text-sm sm:text-[15px] max-w-xl">
            Buy a finished site built on BuildersLab and make it yours — or sell one you built and keep {keep} of every sale.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onSell && (
            <button onClick={onSell} className="h-10 px-4 rounded-full border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-sm font-semibold text-zinc-200">
              Sell a site
            </button>
          )}
          <Link to="/Marketplace" className="h-10 px-4 rounded-full bg-white text-zinc-950 hover:bg-zinc-200 text-sm font-semibold inline-flex items-center gap-1.5">
            Browse all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {data.listings.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.listings.map(l => <ListingCard key={l.id} listing={l} pricing={pricing} />)}
        </div>
      ) : (
        <div className="border border-dashed border-zinc-800 rounded-2xl p-8 text-center">
          <Store className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-300 text-sm font-medium">No sites listed yet — yours could be the first.</p>
        </div>
      )}
    </Tile>
  );
}
